import { useEffect, useRef, useState } from 'react'
import { DocumentHelper } from './document'

const useAutofocus = () => {
  const ref = useRef<any>(null)

  useEffect(() => {
    if (ref.current) {
      ref.current.focus()
    }
  }, [])

  return {
    ref,
  }
}

/**
 * @deprecated Prefer using the "useDebounceHook"
 */
function useDebounceFunction<T extends (...args: any[]) => void>(
  fn: T,
  delay: number,
  runOnDestroy = false,
) {
  const hasRunOnDestroy = useRef(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const argsRef = useRef<Parameters<T> | null>(null)

  const debouncedFn = (...args: Parameters<T>) => {
    argsRef.current = args

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      fn(...args)

      timeoutRef.current = null
      argsRef.current = null
    }, delay)
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current && runOnDestroy && !hasRunOnDestroy.current) {
        hasRunOnDestroy.current = true
        clearTimeout(timeoutRef.current)
        fn(...argsRef.current)
      }
    }
  }, [fn])

  return debouncedFn
}

/**
 * Debounce any functions with control around to manually execute logic
 */
function useDebounceHook<T extends (...args: any[]) => void>(
  fn: T,
  delay: number,
  runOnDestroy = false,
) {
  const hasRunOnDestroy = useRef(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const argsRef = useRef<Parameters<T> | null>(null)

  const [isDirty, setDirty] = useState(false)

  const debouncedFn = (...args: Parameters<T>) => {
    argsRef.current = args

    setDirty(true)

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      fn(...args)

      timeoutRef.current = null
      argsRef.current = null
    }, delay)
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current && runOnDestroy && !hasRunOnDestroy.current) {
        hasRunOnDestroy.current = true
        clearTimeout(timeoutRef.current)
        fn(...argsRef.current)
      }
    }
  }, [fn])

  const execute = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      return fn(...argsRef.current)
    }
  }

  return {
    handler: debouncedFn,
    execute,
    isDirty,
    setDirty,
  }
}

const downloadJSON = (data: any, fileName: string) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${fileName}.json`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export const ToolboxClient = {
  useAutofocus,
  useDebounceFunction,
  useDebounceHook,
  downloadJSON,
  downloadPDF: DocumentHelper.downloadPDF,
  downloadDocx: DocumentHelper.downloadDocx,
}
